import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EasePicker, { AmpPlugin, PresetPlugin, RangePlugin } from "react-easepick";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import api from "../api";
import { ReactComponent as Amex } from "../assets/images/amex.svg";
import { ReactComponent as BankStripe } from "../assets/images/bank_stripe.svg";
import { ReactComponent as BankIcon } from "../assets/images/icons/ic-bank.svg";
import { ReactComponent as LinkIcon } from "../assets/images/icons/ic-link.svg";
import { ReactComponent as CreditCardIcon } from "../assets/images/icons/ic-card.svg";
import { ReactComponent as TestIcon } from "../assets/images/icons/ic-corrections.svg";
import { ReactComponent as FilterIcon } from "../assets/images/icons/ic-filter.svg";
import { ReactComponent as ExportIcon } from "../assets/images/icons/ic-export.svg";
import { ReactComponent as StatsIcon } from "../assets/images/icons/ic-stats.svg";
import { ReactComponent as EmptyIllustration } from "../assets/images/illustrations/il-empty.svg";
import { ReactComponent as Mastercard } from "../assets/images/mastercard.svg";
import { ReactComponent as PayPal } from "../assets/images/paypal.svg";
import { ReactComponent as Scalapay } from "../assets/images/scalapay.svg";
import { ReactComponent as CalendarClock } from "../assets/images/icons/ic-calendar-clock.svg";
import { ReactComponent as Visa } from "../assets/images/visa.svg";
import { CourseType, OrderStatus } from "../common/constants";
import Checkbox from "../components/Checkbox";
import EmptyContent from "../components/EmptyContent";
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout";
import MultipleSelect from "../components/MultipleSelect";
import Pagination from "../components/Pagination";
import Button from "../components/Button";
import Select from "../components/Select";
import StatusBadge from "../components/StatusBadge";
import Table from "../components/Table";
import TextInput from "../components/TextInput";
import { formatPrice, getOrderStatusBadge } from "../utils";
import styles from "./Orders.module.css";
import useContextMenu from "../common/hooks/useContextMenu";
import useWindowDimensions from "../common/hooks/useWindowDimensions";
import { useNavigate } from "react-router-dom";

const ORDER_SORT_OPTIONS = [
    {
        id: "created_at",
        name: "Creazione ordine",
        value: "created_at"
    },
    {
        id: "updated_at",
        name: "Modifica ordine",
        value: "updated_at"
    },
    {
        id: "completed_at",
        name: "Completamento ordine",
        value: "completed_at"
    }
]

const DEFAULT_FILTERS = {
    q: null,
    startDate: null,
    endDate: null,
    products: [],
    status: [],
    sortBy: "created_at",
    sortDirection: "desc",
    test: false
}

const getInitialFilters = () => {
    const queryParams = new URLSearchParams(window.location.search)
    const sortBy = queryParams.get('sort_by')
    const sortDirection = queryParams.get('sort_direction')

    return {
        ...DEFAULT_FILTERS,
        sortBy: ORDER_SORT_OPTIONS.some(option => option.value === sortBy) ? sortBy : DEFAULT_FILTERS.sortBy,
        sortDirection: ["asc", "desc"].includes(sortDirection) ? sortDirection : DEFAULT_FILTERS.sortDirection,
    }
}

const Orders = () => {

    const navigate = useNavigate();
    const { width } = useWindowDimensions()
    const { t } = useTranslation();

    const [showPagination, setShowPagination] = useState(true)
    const [page, setPage] = useState(null)
    const [per, setPer] = useState(null)
    const [orders, setOrders] = useState([])
    const [count, setCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [loadingPage, setLoadingPage] = useState(true)
    const [exportingOrders, setExportingOrders] = useState(false)
    const [filterOpen, setFilterOpen] = useState(false)

    const ORDERS_DEBOUNCE_DELAY = 300

    const ordersRequestControllerRef = useRef(null)
    const ordersRequestIdRef = useRef(null)
    const ordersDebounceTimeoutRef = useRef(null)

    const [filters, _setFilters] = useState(getInitialFilters)
    const filterRef = useRef(filters)
    const setFilters = (newFilters) => {
        filterRef.current = {
            ...filterRef.current,
            ...newFilters
        }
        _setFilters(filterRef.current)
    }

    const prevFiltersRef = useRef(null)
    const prevPageRef = useRef(null)
    const prevPerRef = useRef(null)

    const [courses, setCourses] = useState([])
    const { showMenu } = useContextMenu()

    const options = useMemo(
        () => ({
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
                '/css/easepick-range.css',
            ],
            zIndex: 1000,
            plugins: [
                RangePlugin,
                PresetPlugin,
                AmpPlugin
            ],
            lang: 'it-IT',
            format: "DD MMM YYYY",
            AmpPlugin: {
                resetButton: () => {
                    setFilters({
                        ...filterRef.current,
                        startDate: null,
                        endDate: null
                    })
                    return true
                },
                darkMode: false,
                dropdown: {
                    months: true,
                    years: true,
                    minYear: 2020
                }
            },
            PresetPlugin: {
                customPreset: {
                    'Oggi': [
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '7 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 6); // 6 giorni fa
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Settimana': [
                        (() => {
                            const now = new Date();
                            const day = now.getDay(); // 0 (domenica) - 6 (sabato)
                            const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Lunedì
                            const monday = new Date(now.setDate(diff));
                            return new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    '30 giorni': [
                        (() => {
                            const date = new Date();
                            date.setDate(date.getDate() - 29); // 29 giorni fa
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
                        })(),
                        (() => {
                            const date = new Date();
                            return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
                        })(),
                    ],
                    'Mese': [
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0); // Primo giorno del mese
                        })(),
                        (() => {
                            const now = new Date();
                            return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999); // Ultimo giorno del mese
                        })(),
                    ],
                    'Anno': [
                        new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
                        new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
                    ]
                }
            },
            setup(picker) {
                picker.on("select", (e) => {
                    const { start, end } = e.detail;
                    end.setHours(23, 59, 59, 999)
                    setFilters({
                        ...filterRef.current,
                        startDate: start.toISOString(),
                        endDate: end.toISOString()
                    })
                });
            },
        }),
        []
    );

    useEffect(() => {
        const getCourses = async () => {
            try {
                const courses = await api.get("/admin/courses?complete=false", { timeout: 0 })
                setCourses(courses.filter(c => c.product && c.type !== CourseType.Webinar))
            }
            catch (e) {
                console.error(e)
            }
        }


        getCourses()

        const queryParams = new URLSearchParams(window.location.search);

        const page = queryParams.get('page')
        const per = queryParams.get('per')


        setPage(page ? parseInt(page - 1) : 0)
        setPer(per ? parseInt(per) : 50)

    }, [])

    useEffect(() => {
        if (page !== null && per !== null) {
            const newParams = new URLSearchParams({
                page: page ? page + 1 : 1,
                per: per ? per : 50,
                sort_by: filters.sortBy,
                sort_direction: filters.sortDirection,
            });

            window.history.replaceState(null, '', `?${newParams.toString()}`);
        }
    }, [page, per, filters.sortBy, filters.sortDirection])

    const cancelOrdersRequest = useCallback(() => {
        if (ordersRequestControllerRef.current) {
            ordersRequestControllerRef.current.abort()
            ordersRequestControllerRef.current = null
        }
        ordersRequestIdRef.current = null
    }, [])

    // Fetch orders with optional debounce, cancelling any in-flight request first.
    const getOrders = useCallback(({ debounce = false } = {}) => {
        if (page === null || per === null) {
            cancelOrdersRequest()
            return
        }

        const delay = debounce ? ORDERS_DEBOUNCE_DELAY : 0

        if (ordersDebounceTimeoutRef.current) {
            clearTimeout(ordersDebounceTimeoutRef.current)
            ordersDebounceTimeoutRef.current = null
        }

        cancelOrdersRequest()

        const triggerFetch = () => {
            const controller = new AbortController()
            const requestId = Symbol("ordersRequest")

            ordersRequestControllerRef.current = controller
            ordersRequestIdRef.current = requestId

            const fetchOrders = async () => {
                try {
                    const params = new URLSearchParams();

                    params.append("page", page);
                    params.append("per", per);

                    const activeFilters = filterRef.current

                    if (activeFilters.startDate) {
                        params.append("start_date", activeFilters.startDate);
                    }
                    if (activeFilters.endDate) {
                        params.append("end_date", activeFilters.endDate);
                    }
                    if (activeFilters.products.length > 0) {
                        params.append("products", activeFilters.products.join(","))
                    }
                    if (activeFilters.status.length > 0) {
                        params.append("status", activeFilters.status.join(","));
                    }
                    if (activeFilters.test) {
                        params.append("test", activeFilters.test);
                    }
                    if (activeFilters.q) {
                        params.append("q", activeFilters.q);
                    }
                    if (activeFilters.sortBy) {
                        params.append("sort_by", activeFilters.sortBy);
                    }
                    if (activeFilters.sortDirection) {
                        params.append("sort_direction", activeFilters.sortDirection);
                    }

                    const data = await api.get(`/admin/orders?${params.toString()}`, {
                        signal: controller.signal
                    });

                    if (ordersRequestIdRef.current !== requestId) {
                        return
                    }

                    const { orders, count } = data;

                    setOrders(orders);
                    setCount(count);
                } catch (e) {
                    if (controller.signal.aborted || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
                        return
                    }
                    console.error(e);
                } finally {
                    if (ordersRequestIdRef.current === requestId) {
                        setLoading(false);
                        setLoadingPage(false);
                        ordersRequestControllerRef.current = null;
                        ordersRequestIdRef.current = null;
                    }
                }
            }

            fetchOrders()
        }

        setLoading(true)

        if (delay > 0) {
            ordersDebounceTimeoutRef.current = setTimeout(() => {
                ordersDebounceTimeoutRef.current = null
                triggerFetch()
            }, delay)
        } else {
            triggerFetch()
        }
    }, [cancelOrdersRequest, page, per])

    useEffect(() => {
        if (page === null || per === null) {
            return
        }

        const filtersChanged = prevFiltersRef.current !== filters
        const pageChanged = prevPageRef.current !== page
        const perChanged = prevPerRef.current !== per

        const shouldDebounce = filtersChanged && !pageChanged && !perChanged

        getOrders({ debounce: shouldDebounce })

        prevFiltersRef.current = filters
        prevPageRef.current = page
        prevPerRef.current = per
    }, [filters, page, per, getOrders])

    useEffect(() => {
        return () => {
            if (ordersDebounceTimeoutRef.current) {
                clearTimeout(ordersDebounceTimeoutRef.current)
            }
            cancelOrdersRequest()
        }
    }, [cancelOrdersRequest])

    const products = useMemo(() => {
        if (courses.length === 0) return []

        return courses.filter((course) => course.product && course.type !== CourseType.Webinar).map((course) => {
            return {
                id: course.product?.id,
                name: course.name,
                value: course.product?.id
            }
        }).sort((a, b) => {
            return a.name.localeCompare(b.name)
        })
    }, [courses])

    const orderStatuses = Object.values(OrderStatus).map((status) => {
        return {
            id: status,
            name: t(`paymentStatus.${status}`),
            value: status
        }
    })

    const getPaymentMethodIcon = (order) => {
        if (!order) return;
        if (!order.payment_method) {
            if (order.status === OrderStatus.Succeeded) {
                return <BankIcon className={styles.paymentMethod} />
            }
            else if (order.status === OrderStatus.PartiallyPaid) {
                return <CalendarClock className={styles.paymentMethod} />
            }
            else {
                return;
            }
        }

        switch (order.payment_method) {
            case 'paypal':
                return <PayPal className={styles.paymentMethod} />
            case 'scalapay':
                return <Scalapay className={styles.paymentMethod} />
            case 'customer_balance':
                return <BankStripe className={styles.paymentMethod} />
            case 'visa':
                return <Visa className={styles.paymentMethod} />
            case 'mastercard':
                return <Mastercard className={styles.paymentMethod} />
            case 'amex':
                return <Amex className={styles.paymentMethod} />
            default:
                return <CreditCardIcon className={styles.paymentMethod} />
        }
    }

    const getStatusBadge = (status) => {
        if (!status) {
            return null;
        }
        return getOrderStatusBadge(status)
    }

    const updateOrderTest = async (order) => {
        try {
            await api.put(`/admin/orders/${order.id}`, {
                test: !order.test
            })
            setOrders(orders.map((o) => {
                if (o.id === order.id) {
                    return { ...o, test: !o.test }
                }
                return o
            }))
        }
        catch (error) {
            console.error(error)
        }
    }

    const exportOrdersCsv = useCallback(async () => {
        if (exportingOrders) {
            return
        }

        setExportingOrders(true)
        try {
            const activeFilters = filterRef.current
            const exportPer = 200
            let exportPage = 0
            let total = null
            let allOrders = []

            do {
                const params = new URLSearchParams()
                params.append("page", String(exportPage))
                params.append("per", String(exportPer))

                if (activeFilters.startDate) {
                    params.append("start_date", activeFilters.startDate)
                }
                if (activeFilters.endDate) {
                    params.append("end_date", activeFilters.endDate)
                }
                if (activeFilters.products.length > 0) {
                    params.append("products", activeFilters.products.join(","))
                }
                if (activeFilters.status.length > 0) {
                    params.append("status", activeFilters.status.join(","))
                }
                if (activeFilters.test) {
                    params.append("test", activeFilters.test)
                }
                if (activeFilters.q) {
                    params.append("q", activeFilters.q)
                }
                if (activeFilters.sortBy) {
                    params.append("sort_by", activeFilters.sortBy)
                }
                if (activeFilters.sortDirection) {
                    params.append("sort_direction", activeFilters.sortDirection)
                }

                const data = await api.get(`/admin/orders?${params.toString()}`, { timeout: 0 })
                const batch = data?.orders || []
                total = data?.count ?? batch.length
                allOrders = [...allOrders, ...batch]
                exportPage += 1

                if (batch.length === 0) {
                    break
                }
            } while (allOrders.length < total)

            const headers = [
                "id",
                "order_no",
                "created_at",
                "updated_at",
                "completed_at",
                "user_name",
                "user_surname",
                "user_email",
                "products",
                "amount_eur",
                "status",
                "payment_method",
                "marketing",
                "marketing_updated_at",
                "test",
            ]

            const escapeCsv = (value) => {
                const normalized = String(value ?? "")
                if (/[",\n]/.test(normalized)) {
                    return `"${normalized.replace(/"/g, '""')}"`
                }
                return normalized
            }

            const rows = allOrders.map((order) => {
                const marketingConsent = order?.user?.consents?.marketing
                const marketingValue = marketingConsent?.value === true
                const marketingUpdatedAt = marketingConsent?.updated_at || ""

                return [
                    order?.id,
                    order?.order_no,
                    order?.created_at,
                    order?.updated_at,
                    order?.completed_at,
                    order?.user?.name,
                    order?.user?.surname,
                    order?.user?.email,
                    (order?.products || []).map((p) => p?.name).filter(Boolean).join(" | "),
                    typeof order?.amount === "number" ? formatPrice(order.amount / 100) : "",
                    order?.status,
                    order?.payment_method,
                    marketingValue ? "true" : "false",
                    marketingUpdatedAt,
                    order?.test ? "true" : "false",
                ]
            })

            const csv = [headers, ...rows].map((row) => row.map(escapeCsv).join(",")).join("\n")
            const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url
            link.setAttribute("download", `orders_export_${new Date().toISOString().slice(0, 10)}.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        }
        catch (e) {
            console.error(e)
        }
        setExportingOrders(false)
    }, [exportingOrders])

    const activeFilterCount = Number(Boolean(filters.startDate || filters.endDate)) + Number(filters.products.length > 0) + Number(filters.status.length > 0) + Number(Boolean(filters.test))

    return (
        <HeaderFooterLayout>
            <HelmetProvider>
                <Helmet>
                    <title>
                        Shop - Ordini
                    </title>
                </Helmet>
            </HelmetProvider>
            <div className={styles.container}>
                <div className={styles.section}>
                    <div className={styles.sectionInner}>
                        <div className={styles.pageHeader}>
                            <div className={styles.pageHeading}>
                                <h1>Ordini</h1>
                                <p>Gestisci acquisti, pagamenti e piani rateali.</p>
                            </div>
                            <div className={styles.pageActions}>
                                <Button appearance="subtle" onClick={() => navigate('/reports/orders')}>
                                    <StatsIcon style={{ width: '11px', height: '11px' }} />
                                    Report ordini
                                </Button>
                                <Button appearance="subtle" disabled={exportingOrders} onClick={exportOrdersCsv}>
                                    <ExportIcon style={{ width: '11px', height: '11px' }} />
                                    {exportingOrders ? 'Esportazione...' : 'Esporta CSV'}
                                </Button>
                            </div>
                        </div>

                        <div className={styles.filterSection}>
                            <div className={styles.search}>
                                <label className={styles.searchField}>
                                <span className={styles.visuallyHidden}>Cerca ordini</span>
                                <TextInput
                                    appearance="modern"
                                    value={filters.q}
                                    onKeyUp={(value) => {
                                        setFilters({
                                            ...filters,
                                            q: value
                                        })
                                    }}
                                    type="search"
                                    placeholder="Cerca un ordine…" />
                                </label>
                                <Button appearance="subtle" additionalClass={styles.filterToggle} aria-expanded={filterOpen} aria-controls="order-filters" onClick={() => setFilterOpen(!filterOpen)}>
                                    <FilterIcon aria-hidden="true" /> Filtri
                                    {activeFilterCount > 0 && <span className={styles.filterCount}>{activeFilterCount}</span>}
                                </Button>
                                <span className={styles.resultCount} aria-live="polite">{loading ? 'Caricamento…' : `${count ?? 0} ordini`}</span>
                            </div>
                            <div id="order-filters" className={`${styles.filters} ${filterOpen ? styles.open : ""}`}>
                                <div className={styles.filterField}><span>Periodo</span>
                                <EasePicker options={options} placeholder="Tutte le date"
                                    className={styles.datePicker} /></div>
                                <div className={styles.filterField}><span>Prodotti</span>
                                <MultipleSelect
                                    appearance="modern"
                                    portal
                                    selected={products.filter(p => filters.products.includes(p.id))}
                                    options={products}
                                    // MultipleSelect filters the loaded options internally.
                                    onSearch={() => {}}
                                    searchPlaceholder="Cerca un prodotto…"
                                    customOptions={[
                                        {
                                            id: "masters",
                                            name: "Master",
                                            selected: courses.filter(c => c.type === CourseType.Professional).map(c => c.product.id).every(id => filters.products.includes(id)),
                                            onSelect: (selected) => {
                                                let masters_ids = courses.filter(c => c.type === CourseType.Professional).map(c => c.product.id)
                                                setFilters({
                                                    ...filters,
                                                    products: selected ? [...filters.products, ...masters_ids] : filters.products.filter(p => !masters_ids.includes(p))
                                                })
                                            }
                                        },
                                        {
                                            id: "masterclass",
                                            name: "Masterclass",
                                            selected: courses.filter(c => c.type === CourseType.Masterclass).map(c => c.product.id).every(id => filters.products.includes(id)),
                                            onSelect: (selected) => {
                                                let masters_ids = courses.filter(c => c.type === CourseType.Masterclass).map(c => c.product.id)
                                                setFilters({
                                                    ...filters,
                                                    products: selected ? [...filters.products, ...masters_ids] : filters.products.filter(p => !masters_ids.includes(p))
                                                })
                                            }
                                        },
                                        {
                                            id: "bootcamp",
                                            name: "Bootcamp",
                                            selected: courses.find(c => c.type === CourseType.Bootcamp) ? courses.filter(c => c.type === CourseType.Bootcamp).map(c => c.product.id).every(id => filters.products.includes(id)) : [],
                                            onSelect: (selected) => {
                                                let masters_ids = courses.filter(c => c.type === CourseType.Bootcamp).map(c => c.product.id)
                                                setFilters({
                                                    ...filters,
                                                    products: selected ? [...filters.products, ...masters_ids] : filters.products.filter(p => !masters_ids.includes(p))
                                                })
                                            }
                                        }
                                    ]}
                                    allLabel="Tutti i prodotti"
                                    placeholder="Tutti i prodotti"
                                    onSelect={(items) => {
                                        setFilters({
                                            ...filters,
                                            products: items.map(item => item.id)
                                        })
                                    }} /></div>
                                <div className={styles.filterField}><span>Stato ordine</span>
                                <MultipleSelect
                                    appearance="modern"
                                    portal
                                    selected={orderStatuses.filter(s => filters.status.includes(s.id))}
                                    options={orderStatuses}
                                    placeholder="Tutti gli stati"
                                    allLabel="Tutti gli stati"
                                    onSelect={(items) => {
                                        setFilters({
                                            ...filters,
                                            status: items.map(item => item.value)
                                        })
                                    }} /></div>
                                <div className={styles.filterField}><span>Ordina per</span>
                                <div className={styles.sortFilter}>
                                    <Select
                                        appearance="modern"
                                        portal
                                        style={{ minWidth: 0, width: '100%' }}
                                        options={ORDER_SORT_OPTIONS}
                                        selected={ORDER_SORT_OPTIONS.find(o => o.value === filters.sortBy) ?? ORDER_SORT_OPTIONS[0]}
                                        onSelect={(item) => {
                                            setFilters({
                                                ...filters,
                                                sortBy: item.value
                                            })
                                            setPage(0)
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className={styles.sortDirectionButton}
                                        aria-label={filters.sortDirection === "desc" ? "Ordinamento decrescente" : "Ordinamento crescente"}
                                        title={filters.sortDirection === "desc" ? "Decrescente" : "Crescente"}
                                        onClick={() => {
                                            setFilters({
                                                ...filters,
                                                sortDirection: filters.sortDirection === "desc" ? "asc" : "desc"
                                            })
                                            setPage(0)
                                        }}
                                    >
                                        {filters.sortDirection === "desc" ? "↓" : "↑"}
                                    </button>
                                </div></div>

                                <div className={styles.testFilter}><Checkbox label="Ordini test" size="1.5rem" radius="4px" labelPosition="end" value={filters.test} onChange={(checked) => {
                                    setFilters({
                                        ...filters,
                                        test: checked
                                    })
                                }} style={{ minWidth: 'fit-content' }} /></div>
                            </div>
                        </div>

                        <div className={styles.orders}>
                            <Table
                                key={`${filters.sortBy}-${filters.sortDirection}`}
                                appearance="minimal"
                                loading={loading}
                                loadingN={50}
                                onContextMenu={(e, item) => {
                                    const actions = [
                                        {
                                            name: item.test ? 'Rimuovi test' : 'Segna come test',
                                            icon: <TestIcon />,
                                            onClick: async () => {
                                                await updateOrderTest(item)
                                            }
                                        },
                                        {
                                            name: 'Apri in una nuova scheda',
                                            icon: <LinkIcon />,
                                            onClick: () => {
                                                window.open(`/orders/${item.id}`, '_blank');
                                            }
                                        }
                                    ]
                                    showMenu(e, actions)
                                }}
                                defaultSort={{ key: filters.sortBy, direction: filters.sortDirection }}
                                columns={[
                                    { key: 'order_no', label: 'Ordine', type: 'custom' },
                                    { key: 'user', label: 'Cliente', type: 'custom' },
                                    { key: 'products', label: 'Prodotti', type: 'custom', wrap: true },
                                    { key: 'amount', label: 'Importo', type: 'custom' },
                                    { key: 'status', label: 'Stato', type: 'custom' },
                                    { key: 'payment_method', label: 'Pagamento', type: 'custom' },
                                    { key: 'created_at', label: 'Creato il', type: 'datetime' },
                                    { key: 'updated_at', label: 'Modificato il', type: 'datetime' },
                                    { key: 'completed_at', label: 'Completato il', type: 'datetime' },
                                    { key: 'id', label: 'ID', type: 'number' },
                                ]}
                                elements={orders.map((o) => ({
                                    ...o,
                                    order_no: { value: o.order_no, type: 'string', component: <a className={styles.orderCode} href={`/orders/${o.id}`}>{o.order_no || `#${o.id}`}</a> },
                                    user: { value: `${o.user?.name ?? 'Utente'} ${o.user?.surname ?? 'Eliminato'}`, type: 'string', component: <div className={styles.customerCell}><strong>{`${o.user?.name ?? 'Utente'} ${o.user?.surname ?? 'Eliminato'}`}</strong>{o.user?.email && <span title={o.user.email}>{o.user.email}</span>}</div> },
                                    products: { value: o.products.map((p) => p.name).join(", "), type: 'string', component: <div className={styles.orderProducts} title={o.products.map(p => p.name).join(", ")}>{o.products.map((p) => p.name).join(", ")}</div> },
                                    amount: { value: o.amount, type: 'number', component: <span className={styles.orderAmount}>{formatPrice(o.amount / 100)} €</span> },
                                    payment_method: { value: o.payment_method, type: 'string', component: <div>{getPaymentMethodIcon(o)}</div> },
                                    status: { value: o.status, type: 'string', component: <div className={styles.orderBadges}>{o.test && <StatusBadge appearance="soft" label="Test" color="var(--sf-orange)" />}<StatusBadge appearance="soft" {...getStatusBadge(o.status)} /></div> },
                                    href: `/orders/${o.id}`
                                }))}
                                onRowClick={(e) => {
                                    navigate(`/orders/${e.id}`);
                                }}
                            />
                            {
                                !loading && orders.length === 0 &&
                                <EmptyContent
                                    Illustration={EmptyIllustration}
                                    title={"Nessun ordine trovato"}
                                />
                            }
                        </div>
                        {
                            showPagination &&
                            <Pagination group={width > 540 ? 5 : 0} total={count} per={per} page={page} onChange={p => setPage(p)} loading={loadingPage} />
                        }
                    </div>
                </div>
            </div>
        </HeaderFooterLayout >

    );
};

export default Orders;
